/**
 * MKS-203 — profile avatar API client.
 *
 * Backend contract:
 *   POST   /api/profile/avatar          multipart/form-data, field "file"
 *   DELETE /api/profile/avatar          → { data: { avatarUrl, avatarVersion } }
 *   DELETE /api/admin/users/:id/avatar  → { data: { avatarUrl, avatarVersion } } (admin only)
 */
import { apiUpload, apiDelete } from './client';

export interface AvatarMutationResult {
  avatarUrl: string | null;
  avatarVersion: string | null;
}

/**
 * Upload a cropped avatar image. `apiUpload` takes a `File`; the crop step
 * produces a `Blob` (canvas.toBlob), so we wrap it rather than hand-rolling
 * a second fetch path.
 */
export async function uploadAvatar(blob: Blob): Promise<AvatarMutationResult> {
  const file = new File([blob], 'avatar.webp', { type: blob.type || 'image/webp' });
  const response = await apiUpload<{ data: AvatarMutationResult }>('/profile/avatar', file);
  return response.data;
}

/** Reset to the synced Slack/Google photo, clearing any uploaded override. */
export async function resetAvatar(): Promise<AvatarMutationResult> {
  const response = await apiDelete<{ data: AvatarMutationResult }>('/profile/avatar');
  return response.data;
}

/** Admin-only: remove another user's uploaded avatar override. */
export async function removeUserAvatar(userId: number): Promise<AvatarMutationResult> {
  const response = await apiDelete<{ data: AvatarMutationResult }>(`/admin/users/${userId}/avatar`);
  return response.data;
}
